// ========================================
// Settings Component
// ========================================
// User preferences and customization

import { useState, useEffect, FormEvent } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { getUserSettings, updateUserSettings } from '../../firebase/firestore';
import './Settings.css';

const Settings = () => {
  const { currentUser, userProfile } = useAuth();

  const [guidingPrincipleTerm, setGuidingPrincipleTerm] = useState('Inner Wisdom');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Load settings
  useEffect(() => {
    if (!currentUser) return;

    const loadSettings = async () => {
      try {
        setLoading(true);
        const settings = await getUserSettings(currentUser.uid);
        setGuidingPrincipleTerm(settings.guidingPrincipleTerm);
      } catch (err) {
        console.error('Error loading settings:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [currentUser]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');

    if (!currentUser) return;

    setSaving(true);

    try {
      await updateUserSettings(currentUser.uid, {
        guidingPrincipleTerm: guidingPrincipleTerm.trim() || 'Inner Wisdom',
      });

      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setMessage('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-content">
        {/* Header */}
        <div className="settings-header glass-panel">
          <h1>Settings</h1>
          <p>Customize your Passage experience</p>
        </div>

        {/* Profile Section */}
        <section className="settings-section glass-panel">
          <h2>Profile</h2>

          <div className="setting-item">
            <label>Name</label>
            <div className="setting-value">{userProfile?.name || 'Not set'}</div>
          </div>

          <div className="setting-item">
            <label>Email</label>
            <div className="setting-value">{userProfile?.email || currentUser?.email || 'Not set'}</div>
          </div>
        </section>

        {/* Personalization Section */}
        <section className="settings-section glass-panel">
          <h2>Personalization</h2>

          {message && (
            <div className={message.includes('success') ? 'success-message' : 'error-message'}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="setting-item">
              <label htmlFor="guidingPrinciple">Guiding Principle Term</label>
              <p className="setting-description">
                Customize the terminology used in resentment work. This can be "Higher Power", "Inner Wisdom",
                "Values", "Universe", or any term that resonates with you.
              </p>
              <input
                type="text"
                id="guidingPrinciple"
                value={guidingPrincipleTerm}
                onChange={(e) => setGuidingPrincipleTerm(e.target.value)}
                placeholder="e.g., Inner Wisdom, Higher Power, Values"
                className="glass-input"
              />
            </div>

            <button type="submit" className="glass-button" disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </form>
        </section>

        {/* About Section */}
        <section className="settings-section glass-panel">
          <h2>About Passage</h2>

          <div className="about-content">
            <p>
              <strong>Version:</strong> 1.0.0
            </p>
            <p>
              <strong>Purpose:</strong> Passage is a mental health and emotional intelligence tool designed to
              help you build self-awareness through structured emotional check-ins, gratitude practices, and
              processing difficult emotions.
            </p>
            <p className="privacy-note">
              🔒 Your data is private and secure. All information is stored securely in Firebase and is only
              accessible to you.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Settings;
